import type { IsoDate } from "./types.js";
import { parseDate } from "../../shared/dates.js";

export const APP_TIMEZONE = "Asia/Taipei";

/**
 * 這裡的日期一律是台北當地日，不是 UTC 日。
 *
 * 純粹的日期算術（`parseDate`／`daysBetween`／`addDays`）的來源在
 * `shared/dates.ts`，後端也讀同一份——比照 `plan.ts` 對待期程常數的做法。
 * 這個檔案只留需要讀系統時間或處理 DB 時間戳的那幾支。
 */

export { parseDate, daysBetween, addDays } from "../../shared/dates.js";

/** 取得指定時刻在台北的當地日期。這是「今天」的唯一定義來源。 */
export function todayIso(now: Date = new Date(), timeZone = APP_TIMEZONE): IsoDate {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone, year: "numeric", month: "2-digit", day: "2-digit"
  }).formatToParts(now);
  const get = (type: string) => parts.find(part => part.type === type)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** 顯示用的短日期（M/D）。 */
export function formatDate(value: IsoDate | null | undefined): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat("zh-TW", { month: "numeric", day: "numeric" })
    .format(parseDate(value));
}

/**
 * 顯示用的完整時間，一律換算成 APP_TIMEZONE 的當地時間。
 *
 * 伺服器存的是 UTC 時間戳（`2026-08-06T00:25:47.000Z`），原樣印出來既不是
 * 台灣時間也不是人看的格式。跨日是最容易錯的一種：UTC 還在前一天的時候，
 * 台北已經是隔天了。
 *
 * 拿到空值或壞掉的時間戳一律回「尚未同步」——印 `Invalid Date` 只會讓人
 * 以為是資料壞了。
 */
/**
 * 把資料庫或 API 給的時間戳轉成 Date。
 *
 * SQLite 的 `CURRENT_TIMESTAMP` 是 `YYYY-MM-DD HH:MM:SS`——**UTC，但字串上
 * 沒有任何時區標記**。直接丟給 `Date` 會被當成本地時間，整整差 8 小時，
 * 而且畫面上看起來還很合理（就是一個時間），所以不會有人發現。
 * 補上 `T` 與 `Z` 之後才是它真正的意思。
 *
 * Apple 健康送來的 `measured_at` 本來就是帶 Z 的 ISO 字串，原樣通過。
 */
function toDate(value: string): Date {
  const sqliteStamp = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
  return new Date(sqliteStamp.test(value) ? `${value.replace(" ", "T")}Z` : value);
}

/**
 * 兩個時間戳裡比較晚的那個。都沒有（或都壞掉）就回 null。
 *
 * **不能直接比字串**：資料庫的 `2026-08-06 15:25:47` 與 Apple 健康的
 * `2026-08-06T15:25:47.000Z` 是同一個時刻卻長得完全不一樣，字串比大小
 * 會得到錯的答案。一律換成時間點再比。
 */
export function laterOf(
  a: string | null | undefined,
  b: string | null | undefined
): string | null {
  const at = a ? toDate(a).getTime() : NaN;
  const bt = b ? toDate(b).getTime() : NaN;
  if (Number.isNaN(at) && Number.isNaN(bt)) return null;
  if (Number.isNaN(at)) return b as string;
  if (Number.isNaN(bt)) return a as string;
  return at >= bt ? (a as string) : (b as string);
}

export function formatDateTime(value: string | null | undefined, timeZone = APP_TIMEZONE): string {
  if (!value) return "尚未同步";
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return "尚未同步";
  return new Intl.DateTimeFormat("zh-TW", {
    timeZone,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false
  }).format(date);
}

/** 星期幾。0 是星期日，與 Date.getDay() 一致。 */
export function weekdayOf(value: IsoDate): number {
  return parseDate(value).getDay();
}
